/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

/**
 * Utility functions for handling invitation links
 */
import RouteEnum from "../router/enums/route.enum";

/**
 * Checks if the current URL contains valid invitation parameters
 * @returns {boolean} True if URL has valid invitation parameters
 */
export const hasValidInviteParams = () => {
  try {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const acceptInvite = params.get("accept_invite");
    const token = params.get("token");

    return acceptInvite === "true" && !!token;
  } catch (error) {
    console.error("Error checking invite params:", error);
    return false;
  }
};

/**
 * Single authoritative check for all known invite URL patterns.
 * Use this function everywhere instead of reimplementing the logic inline.
 *
 * Covers:
 *  - Standard path: /accept-invite?accept_invite=true&token=...
 *  - Sub-path variant: /accept-invite/...
 *  - Root redirect: /?accept_invite=true&token=...
 *  - Malformed path: /accept_invite=true&token=...
 *
 * @returns {boolean} True if the current page is any variant of an invite acceptance URL
 */
export const isInviteAcceptanceUrl = () => {
  try {
    const pathname = window.location.pathname;

    if (
      pathname === RouteEnum.ACCEPT_INVITE ||
      pathname.startsWith(`${RouteEnum.ACCEPT_INVITE}/`)
    ) {
      return true;
    }

    if (hasValidInviteParams()) {
      return true;
    }

    if (hasMalformedInviteFormat()) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Constructs a valid accept invite URL with the given token
 * @param {string} token The invitation token
 * @returns {string} The full accept invite URL
 */
export const buildAcceptInviteUrl = (token) => {
  if (!token) return null;
  return `${RouteEnum.ACCEPT_INVITE}?accept_invite=true&token=${token}`;
};

/**
 * Extracts the invitation token from the URL
 * @returns {string|null} The token or null if not found
 */
export const getInviteTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const token = params.get("token");

    return token;
  } catch (error) {
    console.error("Error getting token from URL:", error);
    return null;
  }
};

/**
 * Checks if the URL has a malformed invite format like /accept_invite=true&token=xyz
 * @returns {boolean} True if URL has malformed invite format
 */
export const hasMalformedInviteFormat = () => {
  try {
    const pathname = window.location.pathname;
    const isMalformedPath = pathname.startsWith("/accept_invite=true");

    if (!isMalformedPath) return false;

    // Extract token
    const tokenMatch = pathname.match(/token=([^&]+)/);
    const hasToken = !!tokenMatch;

    return hasToken;
  } catch (error) {
    console.error("Error checking malformed invite format:", error);
    return false;
  }
};

/**
 * Extracts token from malformed URL path like /accept_invite=true&token=xyz
 * @returns {string|null} The token or null
 */
export const getTokenFromMalformedPath = () => {
  try {
    const pathname = window.location.pathname;
    if (!pathname.startsWith("/accept_invite=true")) return null;

    const tokenMatch = pathname.match(/token=([^&]+)/);
    const token = tokenMatch ? tokenMatch[1] : null;

    return token;
  } catch (error) {
    console.error("Error extracting token from malformed path:", error);
    return null;
  }
};

/**
 * Checks if the current URL is the accept-invite page with a token parameter.
 * This handles direct navigation from email links to /accept-invite?token=...
 * @returns {boolean} True if this is an accept-invite URL with token
 */
export const isAcceptInviteUrl = () => {
  try {
    const pathname = window.location.pathname;
    // Check both exact path and path with trailing slash
    const isAcceptInvitePath =
      pathname === RouteEnum.ACCEPT_INVITE ||
      pathname === `${RouteEnum.ACCEPT_INVITE}/`;

    // Check if this is the root path with invite parameters
    const isRootWithInviteParams = pathname === "/" && hasValidInviteParams();

    // Check for malformed URL format
    const hasMalformedFormat = hasMalformedInviteFormat();

    if (!isAcceptInvitePath && !isRootWithInviteParams && !hasMalformedFormat)
      return false;

    // Check if token exists in URL params or malformed path
    const token = getInviteTokenFromUrl() || getTokenFromMalformedPath();

    return !!token;
  } catch (error) {
    console.error("Error checking if accept invite URL:", error);
    return false;
  }
};
